/* eslint-disable react-hooks/exhaustive-deps */
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { hooks as commonHooks } from '@sitebuilder/common';
import RiskIndicator from '../RiskIndicator/RiskIndicator';
import IconIndicator from '../IconIndicator/IconIndicator';
import { isLocalStorageAccessible } from '../utils';
import { FirstColumnOption } from './utils';
import FundNameTableCell from './FundNameTableCell';

interface PropsShape {
    firstColumnOptions?: FirstColumnOption;
    columns: {
        id: string;
        label: string;
        format?: string;
        nullGuard?: string;
        noDataAnnotationId?: string;
        annotationId?: string;
        additionalFormat?: string;
        columnType?: 'risk' | 'price' | 'rating' | 'documents' | 'twoRow' | 'checklist';
    }[];
    data: Record<string, string>;
    checkList: string[];
    setCheckList: (checkList: string[]) => void;
    isLastRow?: boolean;
    fundUrlMapping: {
        parameters: {
            key: string;
            value: string;
        }[];
        fundUrlTag: string;
    }[];
    fundUrl: string;
    fundUrlBase: string;
    fundPdfUrlBase: string;
    fundPdfName: string;
    setFavorites: (favorites: string[]) => void;
    dataContext: any;
    selectedListStorageName?: string;
    selectedListDataId?: string;
    maxSelectionList?: number;
    selectedListJsonPath?: string;
    disableCheckbox?: boolean;
    setDisableCheckbox: (checked: boolean) => void;
    disableTabStaticData?: boolean;
}

export default function TableRow(props: PropsShape) {
    const {
        columns,
        data,
        checkList,
        setCheckList,
        firstColumnOptions,
        isLastRow,
        fundUrlMapping,
        fundUrlBase,
        fundPdfUrlBase,
        fundPdfName,
        dataContext,
        selectedListStorageName,
        selectedListDataId,
        maxSelectionList,
        selectedListJsonPath,
        disableCheckbox,
        setDisableCheckbox,
        disableTabStaticData,
        ...rest
    } = props;

    const i18n = commonHooks.useLocalization();

    const { id, addIconName, removeIconName, addIconURL, removeIconURL, storageName } =
        firstColumnOptions || {};
    const FAVORITES = storageName || 'favorites';

    const selectedListKey = selectedListStorageName || 'selectedlist';

    const [addToFavorite, setAddToFavorite] = useState(true);
    const [addToSelectedList, setAddToSelectedList] = useState(true);
    const _isLocalStorageAccessible: boolean = isLocalStorageAccessible();

    const {
        fundList: { selectedList },
        setters: { setSelectedList },
    } = commonHooks.useAppContext();

    useEffect(() => {
        if (_isLocalStorageAccessible) {
            // check whether item is already added to favourites or not
            const favoriteItems = localStorage.getItem(FAVORITES);

            if (favoriteItems?.includes(data[id || 'id'])) {
                setAddToFavorite(false);
            } else {
                setAddToFavorite(true);
            }

            if (favoriteItems && favoriteItems.length) {
                rest.setFavorites(JSON.parse(favoriteItems));
            } else {
                rest.setFavorites([]);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data[id || 'id'], _isLocalStorageAccessible]);

    useEffect(() => {
        // check whether item is already added to selectedlist or not
        const selectedListObjectItem = localStorage.getItem('selectedList');
        const selectedListObject = selectedListObjectItem ? JSON.parse(selectedListObjectItem) : {};
        if (selectedListObject && selectedListObject[selectedListKey]) {
            const selectedListItems = selectedListObject[selectedListKey];

            if (selectedListItems?.includes(data[selectedListDataId || 'entityId'])) {
                setAddToSelectedList(false);
            } else {
                setAddToSelectedList(true);
            }

            if (selectedListItems && selectedListItems.length) {
                setSelectedList(selectedListItems);
            } else {
                setSelectedList([]);
            }
            // logic to check if a disable class needs to be added to the checkboxes
            const selectionMaxNumber = maxSelectionList || 10;
            setDisableCheckbox(selectedListItems.length >= selectionMaxNumber);
        }
    }, [data.entityId, _isLocalStorageAccessible]);

    // check for changes in the global selected list
    useEffect(() => {
        // checks for localstorage to clear selected list
        const storedList = localStorage.getItem('selectedList');
        if (!storedList && selectedList.length !== 0) {
            setSelectedList([]);
        }
    }, [selectedList]);

    const setFavorite = () => {
        if (_isLocalStorageAccessible) {
            setAddToFavorite((prev) => !prev);

            if (addToFavorite) {
                // handles case for adding favorites
                let items = [data[firstColumnOptions?.id || 'id']];
                const favoriteItems = localStorage.getItem(FAVORITES);
                favoriteItems && items.push(...JSON.parse(favoriteItems));
                items = _.uniq(items);
                localStorage.setItem(FAVORITES, JSON.stringify(items));
            } else {
                // handle case for removing saved items
                const favorites = localStorage.getItem(FAVORITES);
                const favoriteItems = favorites && JSON.parse(favorites);
                favoriteItems && _.remove(favoriteItems, (item) => item === data[id || 'id']);

                if (favoriteItems.length < 1) {
                    // handle case for single saved item
                    localStorage.removeItem(FAVORITES);
                } else {
                    // handle case for when memory holds other saved items
                    localStorage.setItem(FAVORITES, JSON.stringify(favoriteItems));
                }
            }

            const favoriteItems = localStorage.getItem(FAVORITES);

            if (favoriteItems && favoriteItems.length) {
                rest.setFavorites(JSON.parse(favoriteItems || ''));
            } else {
                rest.setFavorites([]);
            }
        }
    };

    const setSelectedLists = () => {
        const selectionMaxNumber = maxSelectionList || 10;
        if (_isLocalStorageAccessible) {
            setAddToSelectedList((prev) => !prev);
            // handles case for adding updated selectedlist inside the slectedtList Object
            let items = [data[selectedListDataId || 'entityId']];
            if (addToSelectedList) {
                const selectedListObjectItem = localStorage.getItem('selectedList');
                const selectedListObject = selectedListObjectItem
                    ? JSON.parse(selectedListObjectItem)
                    : {};
                // checks if the selected object exists with the required array
                if (selectedListObject && selectedListObject[selectedListKey]) {
                    const selectedlistItems = selectedListObject[selectedListKey];
                    selectedlistItems && items.push(...selectedlistItems);
                    items = _.uniq(items);
                    selectedListObject[selectedListKey] = items;
                    if (items.length < selectionMaxNumber) {
                        localStorage.setItem('selectedList', JSON.stringify(selectedListObject));
                        if (selectedlistItems && selectedlistItems.length) {
                            setSelectedList(items);
                        } else {
                            setSelectedList([]);
                        }
                    } else if (items.length === selectionMaxNumber) {
                        localStorage.setItem('selectedList', JSON.stringify(selectedListObject));
                        setSelectedList(items);
                        setDisableCheckbox(true);
                    } else {
                        setDisableCheckbox(true);
                    }
                } else {
                    // handles case for adding new selectedlist inside the selectedtList object
                    const selectedListObject: { [key: string]: any } = localStorage.getItem(
                        'selectedList'
                    )
                        ? JSON.parse(localStorage.getItem('selectedList') || '')
                        : {};
                    selectedListObject[selectedListKey] = items;
                    const selectedlistItems = selectedListObject[selectedListKey];
                    if (items.length <= selectionMaxNumber) {
                        localStorage.setItem('selectedList', JSON.stringify(selectedListObject));
                        if (selectedlistItems && selectedlistItems.length) {
                            setSelectedList(selectedlistItems);
                        } else {
                            setSelectedList([]);
                        }
                    }
                }
            } else {
                // handle case for removing saved items for the slectedtList Object
                const selectedListObjectItem = localStorage.getItem('selectedList');
                const selectedListObject = selectedListObjectItem
                    ? JSON.parse(selectedListObjectItem)
                    : {};
                if (selectedListObject && selectedListObject[selectedListKey]) {
                    const selectedlistItems = selectedListObject[selectedListKey];
                    selectedlistItems &&
                        _.remove(
                            selectedlistItems,
                            (item) => item === data[selectedListDataId || 'entityId']
                        );
                    if (selectedlistItems.length < 1) {
                        delete selectedListObject[selectedListKey];
                    }
                    if (selectedListObjectItem && selectedListObjectItem.length < 1) {
                        // handle case for single saved item
                        localStorage.removeItem('selectedList');
                    } else {
                        // handle case for when memory holds other saved items
                        localStorage.setItem('selectedList', JSON.stringify(selectedListObject));
                    }
                    if (selectedlistItems && selectedlistItems.length) {
                        setSelectedList(selectedlistItems);
                    } else {
                        setSelectedList([]);
                    }
                    setDisableCheckbox(selectedlistItems.length > selectionMaxNumber);
                }
            }
        }
    };

    const formatValue = (value: any, format: string | undefined) => {
        if (format) {
            if (format.startsWith('date')) {
                return i18n.t(value.replace('-', '/'), format, 'dash');
            } else {
                return i18n.t(value, format, 'dash');
            }
        }

        return i18n.t(value, '_', 'dash');
    };

    const annotator = commonHooks.useAnnotator();
    const favoriteTitle = addToFavorite
        ? i18n.t('widgets_fundList_columnHeadings_addTofavorites')
        : i18n.t('widgets_fundList_columnHeadings_removeFromFavorites');

    return (
        <tr className={`row--item ${isLastRow ? 'row--last' : ''}`}>
            {firstColumnOptions?.type === 'favorites' ? (
                <td>
                    <button
                        className={'column-icon'}
                        onClick={setFavorite}
                        aria-label={favoriteTitle}
                    >
                        {firstColumnOptions?.iconType === 'Font-awesome' ? (
                            <i
                                className={`icon ${
                                    addToFavorite ? `favorite ${addIconName}` : `${removeIconName}`
                                }`}
                                title={favoriteTitle}
                            />
                        ) : (
                            <img
                                className={`icon ${addToFavorite ? 'favorite' : ''}`}
                                src={addToFavorite ? addIconURL : removeIconURL}
                                alt={i18n.t('widgets_fundList_columnHeadings_favorites')}
                            />
                        )}
                    </button>
                </td>
            ) : firstColumnOptions?.type === 'checkList' ? (
                <td tabIndex={0}>
                    <input
                        type="checkbox"
                        checked={checkList.includes(data.id)}
                        onChange={(e) => {
                            if (e.target.checked) {
                                setCheckList([...checkList, data.id]);
                            } else {
                                setCheckList(checkList.filter((item) => item !== data.id));
                            }
                        }}
                    />
                </td>
            ) : (
                ''
            )}

            {columns.map((o) => {
                const value = formatValue(data[o.id], o.format);
                let nullGuard = '';
                let noDataMarker: any = '';
                let dataMarker: any = '';

                if (!data[o.id] && o.nullGuard) {
                    nullGuard = i18n.format(o.nullGuard, dataContext);
                }

                if (
                    ((nullGuard && o.noDataAnnotationId) || (value && o.annotationId)) &&
                    !_.isEmpty(annotator)
                ) {
                    try {
                        // eslint-disable-next-line max-len
                        noDataMarker = annotator?.markersByMarkerTargetId(
                            `${o?.noDataAnnotationId}`
                        );
                        dataMarker = annotator?.markersByMarkerTargetId(`${o?.annotationId}`);
                    } catch (e) {
                        console.log(e);
                    }
                }

                const displayValue =
                    (typeof value === 'string' || typeof value === 'number') && !nullGuard
                        ? value + dataMarker
                        : typeof nullGuard === 'string'
                          ? nullGuard + noDataMarker
                          : i18n.t('', '_', 'dash');

                const nullGuardElement = (
                    <span dangerouslySetInnerHTML={{ __html: i18n.t('', '_', 'dash') as string }} />
                );

                if (o.id === 'fundName') {
                    return (
                        <FundNameTableCell
                            key={o.id}
                            column={o}
                            data={data}
                            fundUrlMapping={fundUrlMapping}
                            fundUrl={rest.fundUrl}
                            fundUrlBase={fundUrlBase}
                            dataContext={dataContext}
                        />
                    );
                } else if (o.columnType) {
                    switch (o.columnType) {
                        case 'risk':
                            const riskData = data[o.id] as any;
                            return (
                                <td
                                    key={o.id}
                                    className="col col--collapsible risk-indicator-cell"
                                    data-label={i18n.format(o.label, dataContext)}
                                    data-column-id={o.id}
                                    tabIndex={disableTabStaticData ? -1 : 0}
                                >
                                    <div className="risk-indicator-cell-wrapper">
                                        <RiskIndicator
                                            _data={{ value: { sortOrder: riskData?.sortOrder } }}
                                            tileAriaLabel={riskData?.name}
                                        />
                                        <span className="risk-rating-text">{riskData?.name}</span>
                                    </div>
                                </td>
                            );
                        case 'documents':
                            const documentsData = data[o.id] as any;
                            const fundFacts = documentsData?.find((d: any) => d.code === 'FF');
                            const fundPdfUrl = i18n.format(fundPdfUrlBase, data);
                            const _fundPdfName = i18n.format(fundPdfName, data);

                            return (
                                <td
                                    key={o.id}
                                    className="col col--collapsible documents-cell"
                                    data-label={i18n.format(o.label, dataContext)}
                                    data-column-id={o.id}
                                >
                                    <div className="document-links-wrapper">
                                        <a
                                            href={fundPdfUrl}
                                            id="link"
                                            rel="noreferrer"
                                            target="_blank"
                                        >
                                            <button
                                                className="icon icon-left"
                                                aria-label={
                                                    _fundPdfName ||
                                                    i18n.t('widgets_fundList_fundProfile')
                                                }
                                                tabIndex={-1}
                                            >
                                                <i className="fas fa-file-pdf"></i>
                                            </button>
                                            <span className="link-name" tabIndex={-1}>
                                                {_fundPdfName ||
                                                    i18n.t('widgets_fundList_fundProfile')}
                                            </span>
                                        </a>

                                        {fundFacts && (
                                            <a
                                                href={fundFacts?.url}
                                                id="link"
                                                rel="noreferrer"
                                                target="_blank"
                                            >
                                                <button
                                                    className="icon icon-left"
                                                    aria-label={
                                                        fundFacts?.name ||
                                                        i18n.t('widgets_fundList_fundFacts')
                                                    }
                                                    tabIndex={-1}
                                                >
                                                    <i className="fas fa-file-pdf"></i>
                                                </button>
                                                <span className="link-name" tabIndex={-1}>
                                                    {fundFacts?.name ||
                                                        i18n.t('widgets_fundList_fundFacts')}
                                                </span>
                                            </a>
                                        )}
                                    </div>
                                </td>
                            );
                        case 'price':
                            return (
                                <td
                                    key={o.id}
                                    className="col col--collapsible price-cell"
                                    data-label={i18n.format(o.label, dataContext)}
                                    data-column-id={o.id}
                                >
                                    <span
                                        dangerouslySetInnerHTML={{ __html: displayValue }}
                                        tabIndex={disableTabStaticData ? -1 : 0}
                                    />
                                    <span
                                        title={`${i18n.t(
                                            'widgets_fundList_viewSelection_items_dailyPrices'
                                        )} ${
                                            parseFloat(data[o.id]) > 0
                                                ? i18n.t('widgets_fundList_up')
                                                : i18n.t('widgets_fundList_down')
                                        }`}
                                        tabIndex={disableTabStaticData ? -1 : 0}
                                    >
                                        {parseFloat(data[o.id]) > 0 ? (
                                            <i
                                                className="fas fa-arrow-up"
                                                title={`${i18n.t(
                                                    // eslint-disable-next-line
                                                    'widgets_fundList_viewSelection_items_dailyPrices'
                                                )} ${i18n.t('widgets_fundList_up')}`}
                                            />
                                        ) : (
                                            <i
                                                className="fas fa-arrow-down"
                                                title={`${i18n.t(
                                                    // eslint-disable-next-line
                                                    'widgets_fundList_viewSelection_items_dailyPrices'
                                                )} ${i18n.t('widgets_fundList_down')}`}
                                            />
                                        )}
                                    </span>
                                </td>
                            );
                        case 'checklist':
                            return (
                                <td
                                    key={o.id}
                                    className="col col--collapsible"
                                    data-label={i18n.format(o.label, dataContext)}
                                    data-column-id={o.id}
                                >
                                    <input
                                        className={
                                            disableCheckbox
                                                ? 'selected-checkbox disable'
                                                : 'selected-checkbox'
                                        }
                                        data-testid={'selected-checkbox'}
                                        type="checkbox"
                                        checked={selectedList.includes(
                                            data[selectedListDataId || 'entityId']
                                        )}
                                        onChange={() => setSelectedLists()}
                                        tabIndex={0}
                                    />
                                </td>
                            );
                        case 'rating':
                            const ratingData = data[o.id] as any;
                            return (
                                <td
                                    key={o.id}
                                    className="col col--collapsible rating-cell"
                                    data-label={i18n.format(o.label, dataContext)}
                                    data-column-id={o.id}
                                >
                                    {ratingData ? (
                                        <>
                                            {ratingData?.numFund || ratingData?.numFund === 0 ? (
                                                <span tabIndex={disableTabStaticData ? -1 : 0}>
                                                    {ratingData?.numFund}{' '}
                                                    {i18n.t('widgets_fundList_rated')}
                                                </span>
                                            ) : (
                                                nullGuardElement
                                            )}
                                            {ratingData?.rating || ratingData?.rating === 0 ? (
                                                <IconIndicator
                                                    _data={{ value: ratingData?.rating }}
                                                    activeIconName="fas fa-star"
                                                    inactiveIconName="far fa-star"
                                                    title={`${ratingData?.rating} stars`}
                                                />
                                            ) : (
                                                nullGuardElement
                                            )}
                                        </>
                                    ) : (
                                        nullGuardElement
                                    )}
                                </td>
                            );
                        case 'twoRow':
                            const twoRowData = data[o.id] as any;

                            const displayValue1 = formatValue(twoRowData?.item1, o.format);
                            const displayValue2 = formatValue(
                                twoRowData?.item2,
                                o.additionalFormat
                            );

                            return (
                                <td
                                    key={o.id}
                                    className="col col--collapsible two-row-cell"
                                    data-label={i18n.format(o.label, dataContext)}
                                    data-column-id={o.id}
                                    tabIndex={disableTabStaticData ? -1 : 0}
                                >
                                    <div
                                        className="two-row-cell-item-1"
                                        dangerouslySetInnerHTML={{ __html: displayValue1 }}
                                    />
                                    <div
                                        className="two-row-cell-item-2"
                                        dangerouslySetInnerHTML={{ __html: displayValue2 }}
                                    />
                                </td>
                            );
                        default:
                            return null;
                    }
                } else {
                    return (
                        <td
                            key={o.id}
                            className="col col--collapsible"
                            data-label={i18n.format(o.label, dataContext)}
                            data-column-id={o.id}
                            tabIndex={disableTabStaticData ? -1 : 0}
                        >
                            <span dangerouslySetInnerHTML={{ __html: displayValue }} />
                        </td>
                    );
                }
            })}
        </tr>
    );
}
